import { FC, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Patient } from 'api/account/patients.type';
import { Slot } from 'components/_temp_ui_common/api/getAvailability';
import { Appointment } from 'components/_temp_ui_common/Appointments/types';
import { useCalendarContext } from 'components/_temp_ui_common/Calendar/CalendarProvider';
import { preferredName } from 'components/_temp_ui_common/utilities/preferredName';
import { DancingBendie } from 'components/bendies/Bendie';
import { Button } from 'components/core/button';
import { H4, Text } from 'components/core/text/Text';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { formatInTimeZone } from 'date-fns-tz';
import { useNavigate } from 'react-router-dom';

interface Props {
  slot: Slot;
  appointment: Appointment;
  member: Patient;
  onDone?: () => void;
  buttonLabel?: string;
}

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
`;

export const CalendarConfirmed: FC<Props> = ({
  slot,
  member,
  onDone,
  buttonLabel = 'Done',
}) => {
  const navigate = useNavigate();
  const {
    state: { account },
  } = useCurrentUser();
  const isSelf = account?.id === member.personalAccountId;
  const context = useCalendarContext();

  const date = useMemo(
    () =>
      formatInTimeZone(
        new Date(slot.startTime),
        context.selectedTimeZone,
        'EEEE, LLLL d, yyyy',
      ),
    [context.selectedTimeZone, slot.startTime],
  );
  const time = useMemo(
    () =>
      formatInTimeZone(
        new Date(slot.startTime),
        context.selectedTimeZone,
        'h:mm aa',
      ),
    [context.selectedTimeZone, slot.startTime],
  );

  const handleOnDone = useCallback(() => {
    if (onDone) {
      onDone();
      return;
    }

    navigate('/home');
  }, [navigate, onDone]);

  return (
    <Wrapper>
      <div className="flex flex-col items-center">
        <div style={{ height: 210, width: 160 }}>
          <DancingBendie />
        </div>
        <H4>Hooray!</H4>
        <Text className="text-center" block variant="text4">
          {isSelf ? 'Your' : `${preferredName(member)}'s`} appointment has been
          confirmed
        </Text>
        <br />
        <Text block variant="text3" bold>
          {date}
        </Text>
        <Text block variant="text3" bold>
          {time}
        </Text>
        <Text block variant="text3" bold>
          {context.selectedTimeZone}
        </Text>
        <br />
        <Text block variant="text5">
          An invitation has also been emailed
        </Text>
      </div>
      <Button onClick={handleOnDone}>{buttonLabel}</Button>
    </Wrapper>
  );
};
